// src/Pages/CouponAssignment/BulkAssignCouponModal.jsx

import React, { useState, useEffect } from "react";
import { Modal, Button, Select, Form, notification, Tag, Space, Tooltip, Flex, Typography } from "antd";
import {
    SendOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    MailOutlined,
    AppleFilled,
    AndroidFilled,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import CustomTable from "../../Component/Table";
import { fetchUserForSearchWithPlatform } from "../../Services/user_question_history";
import {
    getAllCoupons,
    sendCouponAssignmentEmail,
    massAssignCoupon,
    getUserCouponAssignments,
    previewCouponAssignment,
} from "../../Services/coupon";

const { Text } = Typography;

// Discount-safe coupon label ("DISCOUNT20 - Discount" instead of "... - null").
const couponLabel = (c) =>
    `${c.code}${c.plan_name ? ` - ${c.plan_name}` : c.coupon_type === "discount" ? " - Discount" : ""}`;

// Display only - platform never decides whether a user gets a code.
const platformIcon = (platform) => {
    if (platform === "ios") return <AppleFilled style={{ fontSize: 14 }} />;
    if (platform === "android") return <AndroidFilled style={{ fontSize: 14, color: "#3ddc84" }} />;
    return <Text type="secondary">-</Text>;
};

const BulkAssignCouponModal = ({ isModalVisible, handleCancel }) => {
    const [form] = Form.useForm();
    const [couponId, setCouponId] = useState(null);
    const [recipientType, setRecipientType] = useState("specific"); // "all" | "specific"
    // The staged list of users the admin has "Added to list" (UI only - nothing
    // is assigned in the DB until "Assign Coupon" / the per-row assign icon).
    const [listedUsers, setListedUsers] = useState([]);
    // Currently-picked users in the select box, before "Add to list" is pressed.
    const [pickedUsers, setPickedUsers] = useState([]);
    const [pickedMeta, setPickedMeta] = useState({}); // { [user_id]: {name, email, platform} }
    const [applyingAll, setApplyingAll] = useState(false);
    const [emailingAll, setEmailingAll] = useState(false);
    const [adding, setAdding] = useState(false);
    // Bumped after "Add to list" to force-remount CustomFilterSelect, so its
    // internal options/search state resets and the dropdown shows the full
    // user list again (not just the last filtered/selected few).
    const [pickerKey, setPickerKey] = useState(0);

    useEffect(() => {
        if (isModalVisible) {
            form.resetFields();
            setCouponId(null);
            setRecipientType("specific");
            setListedUsers([]);
            setPickedUsers([]);
            setPickedMeta({});
            setPickerKey((k) => k + 1);
        }
    }, [isModalVisible, form]);

    const { data: couponData, isLoading: couponsLoading } = useQuery({
        queryKey: ["active-coupons-for-mass-assign"],
        queryFn: () => getAllCoupons({ isActive: true, limit: 100 }),
        enabled: isModalVisible,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const coupons = couponData?.coupons || [];

    // CustomFilterSelect gives us (key, value[]) where value[] is the user id
    // list. Name/email/platform are captured via renderOption into pickedMeta.
    const onPickChange = (key, value) => {
        setPickedUsers(Array.isArray(value) ? value.filter((a) => Number.isInteger(a)) : []);
    };

    // Add the currently-picked users to the staged list.
    //
    // First runs a DRY RUN (preview) - it writes nothing but tells us whether
    // this coupon draws from a store-code pool and whether that pool has enough
    // codes left. If it is short we stop here: there is no import UI, codes are
    // loaded into the DB in SQL, so the admin must load more or pick fewer users.
    //
    // For each user we then check whether the SELECTED coupon is already
    // assigned to them (via getUserCouponAssignments) - if so the row loads
    // pre-assigned with its existing assignment_id + email_sent, so Email works
    // immediately without re-assigning.
    const handleAddToList = async () => {
        if (!couponId) {
            return notification.error({ message: "Please select a coupon first" });
        }
        const valid = [...pickedUsers].filter((a) => Number.isInteger(a));
        if (valid.length === 0) {
            return notification.error({ message: "Please select at least one valid user" });
        }
        const existingIds = new Set(listedUsers.map((u) => u.user_id));
        const toAdd = valid.filter((id) => !existingIds.has(id));
        if (toAdd.length === 0) {
            return notification.info({ message: "Those users are already in the list." });
        }

        setAdding(true);
        try {
            const preview = await previewCouponAssignment({ coupon_id: couponId, user_ids: toAdd });
            if (!preview) return;

            // Not enough codes in the pool - stop. Falling back to the shared
            // coupon code would hand users a code the admin never intended.
            if (!preview.has_enough_codes) {
                return notification.error({
                    message: "Not enough store codes",
                    description:
                        `${preview.users_needing_codes} user(s) selected but only ` +
                        `${preview.available_store_codes} code(s) left in ` +
                        `${preview.coupon_code}'s pool. Load more codes into the ` +
                        `database, or select fewer users.`,
                    duration: 6,
                });
            }

            // The preview echoes back name/email/platform, so we prefer it over
            // pickedMeta (which is only populated for options actually rendered).
            const previewUsers = new Map((preview.users || []).map((u) => [u.user_id, u]));

            const lookups = await Promise.all(
                toAdd.map(async (id) => {
                    try {
                        const res = await getUserCouponAssignments({ user_id: id });
                        const match = (res?.assignments || []).find((a) => a.coupon_id === couponId);
                        return { id, match };
                    } catch {
                        return { id, match: null };
                    }
                })
            );

            const additions = lookups.map(({ id, match }) => {
                const pv = previewUsers.get(id);
                const base = {
                    user_id: id,
                    name: pv?.name || pickedMeta[id]?.name || `User #${id}`,
                    email: pv?.email || pickedMeta[id]?.email || "",
                    platform: pv?.platform ?? pickedMeta[id]?.platform ?? null,
                    code: null,
                    assignStatus: "pending", // pending | assigning | assigned | already | failed
                    emailStatus: "idle", // idle | sending | sent | failed
                    reason: null,
                    assignment_id: null,
                    isRedeemed: false,
                };
                if (match) {
                    // Already assigned earlier - load as assigned so Email works.
                    return {
                        ...base,
                        assignStatus: "assigned",
                        assignment_id: match.assignment_id,
                        emailStatus: match.email_sent ? "sent" : "idle",
                        isRedeemed: !!match.is_redeemed,
                        reason: match.is_redeemed ? "Already redeemed" : null,
                    };
                }
                return base;
            });

            setListedUsers((prev) => [...prev, ...additions]);
            // Reset the picker AND remount it (pickerKey bump) so its internal
            // search/options state clears - the dropdown shows all users again.
            setPickedUsers([]);
            setPickerKey((k) => k + 1);
        } finally {
            setAdding(false);
        }
    };

    const patchUser = (user_id, patch) =>
        setListedUsers((prev) => prev.map((u) => (u.user_id === user_id ? { ...u, ...patch } : u)));

    // Apply a mass_assign per-user result onto a staged row. The mass_assign
    // endpoint is what detects "Already assigned" / "Already redeemed" / an
    // exhausted code pool, so single assigns route through it too (recipient_type
    // "specific" with one id) rather than duplicating that logic here.
    const applyAssignResult = (r) => {
        if (r.success) {
            patchUser(r.user_id, {
                assignStatus: "assigned",
                assignment_id: r.assignment_id,
                code: r.code || null,
                reason: null,
            });
            return true;
        }
        const already = (r.reason || "").toLowerCase().includes("already");
        patchUser(r.user_id, {
            assignStatus: already ? "already" : "failed",
            reason: r.reason || "Assign failed",
        });
        return false;
    };

    // Step 1: assign the coupon to a single user.
    const assignOne = async (row) => {
        patchUser(row.user_id, { assignStatus: "assigning", reason: null });
        const res = await massAssignCoupon({
            coupon_id: couponId,
            recipient_type: "specific",
            user_ids: [row.user_id],
        });
        const r = res?.results?.[0];
        if (!r) {
            patchUser(row.user_id, { assignStatus: "failed", reason: "Assign failed" });
            return null;
        }
        applyAssignResult(r);
        return r.success ? r.assignment_id : null;
    };

    // Step 2: send (or resend) the coupon email for an already-assigned user.
    const emailOne = async (row) => {
        if (!row.assignment_id) return false;
        patchUser(row.user_id, { emailStatus: "sending" });
        try {
            const res = await sendCouponAssignmentEmail({ assignment_id: row.assignment_id });
            const sent = !!res?.email_sent;
            patchUser(row.user_id, {
                emailStatus: sent ? "sent" : "failed",
                reason: sent ? null : "Email not sent",
            });
            return sent;
        } catch {
            patchUser(row.user_id, { emailStatus: "failed", reason: "Email not sent" });
            return false;
        }
    };

    // Top button: assign the coupon to everyone still assignable in the staged
    // list (assign only - email is a separate step). One mass_assign call for the
    // whole batch, so already-assigned/redeemed users are detected and skipped.
    const handleAssignAll = async () => {
        if (!couponId) {
            return notification.error({ message: "Please select a coupon first" });
        }
        const pending = listedUsers.filter(
            (u) => u.assignStatus !== "assigned" && u.assignStatus !== "already"
        );
        if (pending.length === 0) {
            return notification.info({ message: "Everyone in the list is already assigned." });
        }
        setApplyingAll(true);
        pending.forEach((u) => patchUser(u.user_id, { assignStatus: "assigning", reason: null }));
        const res = await massAssignCoupon({
            coupon_id: couponId,
            recipient_type: "specific",
            user_ids: pending.map((u) => u.user_id),
        });
        setApplyingAll(false);
        if (!res) {
            pending.forEach((u) =>
                patchUser(u.user_id, { assignStatus: "failed", reason: "Assign failed" })
            );
            return;
        }
        (res.results || []).forEach(applyAssignResult);
        notification.success({
            message: `Assigned ${res.coupon_code || couponCode} to ${res.success_count} of ${res.total} users.`,
        });
    };

    // All Users mode: one shot - mass-assign to every user via the backend, then
    // load the returned per-user results into the same table, so the Email step
    // works the same as in Specific mode.
    const handleAssignAllUsers = async () => {
        if (!couponId) {
            return notification.error({ message: "Please select a coupon first" });
        }
        setApplyingAll(true);
        const res = await massAssignCoupon({ coupon_id: couponId, recipient_type: "all", user_ids: [] });
        setApplyingAll(false);
        if (!res) return;
        const rows = (res.results || []).map((r) => {
            const already = !r.success && (r.reason || "").toLowerCase().includes("already");
            return {
                user_id: r.user_id,
                name: r.name || `User #${r.user_id}`,
                email: r.email || "",
                platform: r.platform ?? null,
                code: r.code || null,
                assignStatus: r.success ? "assigned" : already ? "already" : "failed",
                emailStatus: "idle",
                reason: r.success ? null : r.reason || "Not assigned",
                assignment_id: r.assignment_id || null,
                isRedeemed: false,
            };
        });
        setListedUsers(rows);
        notification.success({
            message: `Assigned ${res.coupon_code || couponCode} to ${res.success_count} of ${res.total} users.`,
        });
    };

    // Right button: send (or resend) the coupon email to every assigned row that
    // has an assignment_id (assigned now OR already-assigned earlier). Redeemed
    // users are skipped.
    const handleEmailAll = async () => {
        const assigned = listedUsers.filter(
            (u) => u.assignStatus === "assigned" && u.assignment_id && !u.isRedeemed
        );
        if (assigned.length === 0) {
            return notification.info({ message: "Assign the coupon first, then send the email." });
        }
        setEmailingAll(true);
        let ok = 0;
        for (const row of assigned) {
            // eslint-disable-next-line no-await-in-loop
            const sent = await emailOne(row);
            if (sent) ok += 1;
        }
        setEmailingAll(false);
        notification.success({ message: `Emailed ${couponCode} to ${ok} of ${assigned.length} users.` });
    };

    // Remove a user from the staged list (UI only).
    const removeFromList = (user_id) => {
        setListedUsers((prev) => prev.filter((u) => u.user_id !== user_id));
    };

    const selectedCoupon = coupons.find((c) => c.id === couponId);
    const couponCode = selectedCoupon?.code || "coupon";

    const statusTag = (row) => {
        const s = { whiteSpace: "nowrap", margin: 0 };
        if (row.assignStatus === "assigning") return <Tag color="blue" style={s}>Assigning...</Tag>;
        if (row.assignStatus === "already") return <Tag color="gold" style={s}>Already assigned</Tag>;
        if (row.assignStatus === "failed") return <Tag color="red" style={s}>{row.reason || "Assign failed"}</Tag>;
        if (row.assignStatus === "assigned") {
            if (row.isRedeemed) return <Tag color="purple" style={s}>Redeemed</Tag>;
            if (row.emailStatus === "sending") return <Tag color="blue" style={s}>Emailing...</Tag>;
            if (row.emailStatus === "sent") return <Tag color="green" style={s}>Assigned + Emailed</Tag>;
            if (row.emailStatus === "failed") return <Tag color="orange" style={s}>Assigned, email failed</Tag>;
            return <Tag color="cyan" style={s}>Assigned</Tag>;
        }
        return <Tag style={s}>Pending</Tag>;
    };

    const columns = [
        { title: "ID", dataIndex: "user_id", key: "user_id", width: 55 },
        { title: "Username", dataIndex: "name", key: "name", width: 115 },
        { title: "Email", dataIndex: "email", key: "email", width: 180, render: (e) => e || "-" },
        {
            title: "Platform",
            dataIndex: "platform",
            key: "platform",
            width: 70,
            align: "center",
            render: (p) => platformIcon(p),
        },
        {
            // The code this user actually received - their own store code when
            // the coupon draws from a pool, otherwise the shared coupon code.
            title: "Code",
            dataIndex: "code",
            key: "code",
            width: 110,
            render: (c) => (c ? <Text strong>{c}</Text> : "-"),
        },
        { title: "Status", key: "status", width: 145, render: (_, row) => statusTag(row) },
        {
            title: "Action",
            key: "action",
            width: 130,
            align: "center",
            render: (_, row) => {
                const isAssigned = row.assignStatus === "assigned";
                const isAlready = row.assignStatus === "already";
                return (
                    <Space size="small">
                        {/* 1) Assign - DB assign (retry on failure). Already-assigned
                            rows show a check and cannot be re-assigned. */}
                        <Tooltip
                            title={
                                isAssigned
                                    ? "Assigned"
                                    : isAlready
                                    ? "Already assigned"
                                    : row.assignStatus === "failed"
                                    ? "Retry assign"
                                    : "Assign coupon"
                            }
                        >
                            {isAssigned || isAlready ? (
                                <CheckCircleOutlined
                                    style={{ color: isAlready ? "#d4a017" : "#52c41a", fontSize: 18 }}
                                />
                            ) : (
                                <Button
                                    type="text"
                                    shape="circle"
                                    loading={row.assignStatus === "assigning"}
                                    danger={row.assignStatus === "failed"}
                                    icon={<SendOutlined />}
                                    disabled={!couponId}
                                    onClick={() => assignOne(row)}
                                />
                            )}
                        </Tooltip>

                        {/* 2) Email - send / resend. Enabled for any assigned row
                            with an assignment_id. Redeemed users are skipped. */}
                        <Tooltip
                            title={
                                row.isRedeemed
                                    ? "Already redeemed - cannot email"
                                    : !isAssigned || !row.assignment_id
                                    ? "Assign first"
                                    : row.emailStatus === "sent"
                                    ? "Resend email"
                                    : "Send email"
                            }
                        >
                            <Button
                                type="text"
                                shape="circle"
                                loading={row.emailStatus === "sending"}
                                danger={row.emailStatus === "failed"}
                                icon={
                                    <MailOutlined
                                        style={row.emailStatus === "sent" ? { color: "#52c41a" } : undefined}
                                    />
                                }
                                disabled={!isAssigned || !row.assignment_id || row.isRedeemed}
                                onClick={() => emailOne(row)}
                            />
                        </Tooltip>

                        {/* 3) Remove from list (UI only) */}
                        <Tooltip title="Remove from list">
                            <Button
                                type="text"
                                shape="circle"
                                danger
                                icon={<CloseCircleOutlined />}
                                disabled={row.assignStatus === "assigning" || row.emailStatus === "sending"}
                                onClick={() => removeFromList(row.user_id)}
                            />
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    const assignedCount = listedUsers.filter((u) => u.assignStatus === "assigned").length;
    // "All Assigned" once no row is still assignable (assigned OR already-assigned).
    const assignableLeft = listedUsers.filter(
        (u) => u.assignStatus !== "assigned" && u.assignStatus !== "already"
    ).length;
    const allAssigned = listedUsers.length > 0 && assignableLeft === 0;
    // Send-Email button state: any assigned row with an assignment_id is
    // emailable, except redeemed ones. Label flips to "Resend" once every
    // emailable row has been emailed.
    const assignedRows = listedUsers.filter(
        (u) => u.assignStatus === "assigned" && u.assignment_id && !u.isRedeemed
    );
    const allEmailed = assignedRows.length > 0 && assignedRows.every((u) => u.emailStatus === "sent");

    return (
        <Modal
            title="Bulk Assign Coupon"
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            width={900}
            maskClosable={false}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Coupon / Offer"
                    name="coupon_id"
                    rules={[{ required: true, message: "Coupon is required" }]}
                >
                    <Select
                        placeholder="Select a coupon"
                        loading={couponsLoading}
                        value={couponId}
                        // The STAGED list must go: every row belongs to the
                        // coupon it was built for, and its assignment_id points
                        // at THAT coupon's assignment - keeping it would leave
                        // stale rows whose Email button acts on the old coupon.
                        //
                        // The PICKED ids must NOT go: they are just user ids
                        // nobody has added yet, tied to no coupon. Clearing them
                        // here dropped a selection made BEFORE the coupon was
                        // chosen, while the tags stayed on screen (the picker is
                        // not remounted here), so "Add to list" then reported no
                        // valid users.
                        onChange={(v) => {
                            setCouponId(v);
                            setListedUsers([]);
                        }}
                        options={coupons.map((c) => ({ label: couponLabel(c), value: c.id }))}
                    />
                </Form.Item>

                <Form.Item label="Recipient Type">
                    <Select
                        value={recipientType}
                        onChange={(val) => {
                            setRecipientType(val);
                            setListedUsers([]);
                            setPickedUsers([]);
                            setPickerKey((k) => k + 1);
                        }}
                        options={[
                            { label: "All Users", value: "all" },
                            { label: "Specific Users", value: "specific" },
                        ]}
                    />
                </Form.Item>

                {recipientType === "specific" && (
                    <>
                        {/* No `extra` note here - CustomFilterSelect already
                            renders its own copy-paste hint line. */}
                        {/* Deliberately NOT bound to the Form (no `name`): this
                            picker's state lives in pickedUsers. Form.Item would
                            clone the child with its OWN value/onChange, and
                            because CustomFilterSelect emits onChange(filterKey,
                            value) the Form's defaultGetValueFromEvent takes the
                            FIRST arg - storing the string "user" and injecting
                            value="user" back over the real id array. */}
                        <Form.Item label="Select Specific Users">
                            <CustomFilterSelect
                                key={pickerKey}
                                fetchOptions={fetchUserForSearchWithPlatform}
                                label="Users"
                                filterKey="user"
                                onChange={onPickChange}
                                renderOption={(option) => {
                                    // capture meta so the staged list can show
                                    // name/email/platform without another fetch
                                    if (option?.value != null) {
                                        pickedMeta[option.value] = {
                                            name: option.label,
                                            email: option.email,
                                            platform: option.platform,
                                        };
                                    }
                                    return (
                                        <span>
                                            #{option.value} {option.label} ({option.email}){" "}
                                            {option.platform === "ios" ? (
                                                <AppleFilled style={{ fontSize: 13 }} />
                                            ) : option.platform === "android" ? (
                                                <AndroidFilled style={{ fontSize: 13, color: "#3ddc84" }} />
                                            ) : null}
                                        </span>
                                    );
                                }}
                                className="custom-user-select"
                                mode="multiple"
                                value={pickedUsers}
                                copyPaste={true}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" loading={adding} onClick={handleAddToList}>
                                Add to list
                            </Button>
                        </Form.Item>
                    </>
                )}

                {recipientType === "all" && (
                    <Form.Item>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={applyingAll}
                            disabled={!couponId}
                            onClick={handleAssignAllUsers}
                        >
                            Assign All Users
                        </Button>
                    </Form.Item>
                )}
            </Form>

            {listedUsers.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                        <Space size="middle">
                            <span style={{ fontWeight: 600 }}>List for {couponCode}:</span>
                            <Tag color="green">Assigned: {assignedCount}</Tag>
                            <Tag>Total: {listedUsers.length}</Tag>
                        </Space>
                        <Space size="small">
                            {recipientType === "specific" && (
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    loading={applyingAll}
                                    disabled={allAssigned || !couponId}
                                    onClick={handleAssignAll}
                                >
                                    {allAssigned ? "All Assigned" : "Assign Coupon"}
                                </Button>
                            )}
                            <Button
                                type={recipientType === "all" ? "primary" : "default"}
                                icon={<MailOutlined />}
                                loading={emailingAll}
                                disabled={assignedRows.length === 0}
                                onClick={handleEmailAll}
                            >
                                {allEmailed ? "Resend" : "Send Email"}
                            </Button>
                        </Space>
                    </Flex>
                    {/* Scoped override: the shared CustomTable forces
                        white-space:nowrap + min-width:100px on every cell, which
                        makes long emails push the table wider than the modal and
                        show a horizontal scrollbar. Inside this wrapper we let
                        cells wrap and drop the min-width so it fits the modal. */}
                    <div className="bulk-assign-table">
                        <style>{`
                            .bulk-assign-table .ant-table { white-space: normal; }
                            .bulk-assign-table .ant-table-tbody > tr > td,
                            .bulk-assign-table .ant-table-thead > tr > th {
                                white-space: normal;
                                word-break: break-word;
                                min-width: 0;
                                vertical-align: middle;
                            }
                            .bulk-assign-table .ant-table-content { overflow-x: hidden; }
                        `}</style>
                        <CustomTable
                            columns={columns}
                            data={listedUsers}
                            rowKey={(r) => r.user_id}
                            pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                            }}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default BulkAssignCouponModal;
